import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { DashboardShell } from '../components/brand/DashboardShell';
import {
  FileSpreadsheet, Upload, CheckCircle2, AlertTriangle, RefreshCw,
  ShieldCheck, Activity, Users, Banknote, LayoutGrid, ArrowRight,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const money = (cents) =>
  `$${((cents || 0) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const SAMPLE_EDI = `CLP*CLM-2026-000625*1*150.00*150.00*0*Aetna*~
CLP*CLM-2026-000636*2*75.00*45.00*30.00*Aetna*~
CLP*UNKNOWN-CLAIM*1*100.00*100.00*0*Aetna*~`;

/**
 * /clinician/era — Blueprint Decision #28.
 *
 * Paste a raw 835 EDI string (CLP segments), the backend parses + auto-
 * matches by claim_number + posts payments. Recent ingestions render in
 * the right panel with match stats.
 */
export default function EraIngest() {
  const { getAuthHeader } = useAuth();
  const [form, setForm] = useState({
    payer_name: 'Aetna',
    check_number: '',
    check_date: new Date().toISOString().slice(0, 10),
    raw_edi: SAMPLE_EDI,
  });
  const [result, setResult] = useState(null);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadRecent = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/rcm/era/recent`, getAuthHeader());
      setRecent(data.files || []);
    } catch (e) {
      // silent — empty list is fine
    }
  }, [getAuthHeader]);

  useEffect(() => { loadRecent(); }, [loadRecent]);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async () => {
    if (!form.payer_name || !form.raw_edi.trim()) {
      toast.error('Payer name and EDI text are required.');
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const { data } = await axios.post(`${API}/rcm/era/ingest`, form, getAuthHeader());
      setResult(data);
      await loadRecent();
      toast.success(`Posted ${data.n_matched} of ${data.n_remits} remit${data.n_remits !== 1 ? 's' : ''}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Ingest failed');
    } finally {
      setLoading(false);
    }
  };

  const navItems = [
    { icon: LayoutGrid, label: 'ERA · 835 Ingest', active: true },
    { icon: ShieldCheck, label: 'Trust Portal',  href: '/trust' },
    { icon: Activity,    label: 'Executive',     href: '/operations/executive' },
    { icon: Users,       label: 'Workforce',     href: '/agency/find-clinicians' },
    { icon: Banknote,    label: 'Revenue Cycle', href: '/clinician/billing' },
  ];

  return (
    <DashboardShell
      navItems={navItems}
      title="ERA Auto-Posting"
      subtitle="Paste an 835 EDI remit and CaraLoom matches it to your open claims and posts payments automatically."
      testId="era-ingest-page"
    >
      <div className="grid lg:grid-cols-12 gap-6">
        {/* LEFT — INGEST FORM */}
        <div className="lg:col-span-7">
          <div className="cn-card p-6" data-testid="era-ingest-form">
            <div className="grid grid-cols-3 gap-3 mb-4">
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-cn-text-muted mb-1.5" htmlFor="era-payer">Payer</label>
                <input
                  id="era-payer"
                  className="cn-input w-full"
                  value={form.payer_name}
                  onChange={update('payer_name')}
                  data-testid="era-payer"
                />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-cn-text-muted mb-1.5" htmlFor="era-check">Check #</label>
                <input
                  id="era-check"
                  className="cn-input w-full"
                  value={form.check_number}
                  onChange={update('check_number')}
                  data-testid="era-check"
                />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-cn-text-muted mb-1.5" htmlFor="era-date">Check date</label>
                <input
                  id="era-date"
                  type="date"
                  className="cn-input w-full"
                  value={form.check_date}
                  onChange={update('check_date')}
                  data-testid="era-date"
                />
              </div>
            </div>
            <label className="block text-xs font-bold uppercase tracking-wider text-cn-text-muted mb-1.5" htmlFor="era-edi">
              Raw 835 EDI (CLP segments)
            </label>
            <textarea
              id="era-edi"
              className="cn-input w-full font-mono text-[12px] leading-relaxed"
              rows={10}
              value={form.raw_edi}
              onChange={update('raw_edi')}
              data-testid="era-edi"
              placeholder="CLP*<claim_number>*<status_code>*<charged>*<paid>*<patient_resp>*<payer>*~"
            />
            <p className="text-[11px] text-cn-text-muted mt-2">
              V1 parser handles CLP segments only. Production switches to the Stedi 835 SDK once the contract closes.
            </p>
            <div className="mt-4 flex gap-2">
              <Button
                onClick={submit}
                disabled={loading}
                className="cn-btn cn-btn-primary"
                data-testid="era-submit"
              >
                <Upload className="w-4 h-4" />
                {loading ? 'Posting…' : 'Ingest & post payments'}
              </Button>
            </div>
          </div>

          {/* RESULT */}
          {result && (
            <div className="cn-card p-6 mt-4" data-testid="era-result">
              <div className="grid grid-cols-4 gap-3 mb-5">
                <div>
                  <div className="text-[10px] uppercase tracking-wider font-bold text-cn-text-muted">Remits</div>
                  <div className="font-display font-bold text-2xl text-cn-text" data-testid="era-result-remits">{result.n_remits}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider font-bold text-emerald-700">Matched</div>
                  <div className="font-display font-bold text-2xl text-emerald-700" data-testid="era-result-matched">{result.n_matched}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider font-bold text-amber-700">Unmatched</div>
                  <div className="font-display font-bold text-2xl text-amber-700" data-testid="era-result-unmatched">{result.n_unmatched}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider font-bold text-cn-primary">Posted</div>
                  <div className="font-display font-bold text-2xl text-cn-primary" data-testid="era-result-posted">{money(result.total_posted_cents)}</div>
                </div>
              </div>
              <ul className="space-y-2" data-testid="era-result-list">
                {result.results.map((r, i) => (
                  <li
                    key={i}
                    className={`flex items-center gap-3 px-4 py-2.5 rounded-xl border ${
                      r.matched ? 'border-emerald-200 bg-emerald-50/30' : 'border-amber-200 bg-amber-50/30'
                    }`}
                    data-testid={`era-result-row-${i}`}
                  >
                    {r.matched ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <AlertTriangle className="w-4 h-4 text-amber-600" />}
                    <span className="font-mono text-sm text-cn-text flex-1">{r.claim_number}</span>
                    {r.matched ? (
                      <>
                        <span className="text-sm text-cn-text-muted">Posted</span>
                        <span className="font-semibold text-cn-primary">{money(r.posted_cents)}</span>
                        <span className="text-xs text-cn-text-muted">→</span>
                        <span className="text-xs font-semibold text-cn-text-muted">{r.new_status}</span>
                      </>
                    ) : (
                      <span className="text-sm text-amber-700">{r.reason}</span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* RIGHT — RECENT INGESTIONS */}
        <div className="lg:col-span-5" data-testid="era-recent-panel">
          <div className="cn-card p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display font-semibold text-lg text-cn-text dark:text-white">Recent ingestions</h3>
              <button
                type="button"
                onClick={loadRecent}
                className="cn-btn cn-btn-ghost !py-1.5 !px-2"
                aria-label="Refresh recent list"
                data-testid="era-recent-refresh"
              >
                <RefreshCw className="w-4 h-4" />
              </button>
            </div>
            {recent.length === 0 ? (
              <p className="text-sm text-cn-text-muted py-6 text-center" data-testid="era-recent-empty">No ERA files ingested yet.</p>
            ) : (
              <ul className="space-y-2" data-testid="era-recent-list">
                {recent.map((f, i) => (
                  <li
                    key={f.id || i}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-xl border border-cn-border"
                    data-testid={`era-recent-row-${i}`}
                  >
                    <FileSpreadsheet className="w-4 h-4 text-cn-primary shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm text-cn-text truncate">{f.payer_name}</div>
                      <div className="text-[11px] text-cn-text-muted truncate">
                        {f.n_matched}/{f.n_remits} matched · {money(f.total_posted_cents)} posted
                      </div>
                    </div>
                    <span className="text-[11px] text-cn-text-muted shrink-0">
                      {new Date(f.ingested_at).toLocaleDateString()}
                    </span>
                    <ArrowRight className="w-3 h-3 text-cn-text-muted/60 shrink-0" />
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </DashboardShell>
  );
}
